'use client';

import React, { memo, useState } from "react";
import { motion } from "framer-motion";

interface CryptoIconProps {
  src: string;
  alt: string;
  index: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  totalIcons: number;
  isMobile: boolean;
}

const CryptoIcon = memo(({ src, alt, index, isHovered, onMouseEnter, onMouseLeave, totalIcons, isMobile }: CryptoIconProps) => {
  const isBitcoin = alt.toLowerCase().includes('bitcoin');
  const isKaspa = alt.toLowerCase().includes('kaspa');
  const isAlph = alt.toLowerCase().includes('alephium');

  // Responsive sizes - Bitcoin is larger
  const iconSize = isMobile 
    ? isBitcoin ? "w-20 h-20" : "w-16 h-16"
    : isBitcoin ? "w-24 h-24" : "w-20 h-20";
  
  const iconInnerSize = isMobile 
    ? isBitcoin ? "w-12 h-12" : "w-10 h-10"
    : isBitcoin ? "w-14 h-14" : "w-12 h-12";

  // Different colors for each coin
  const getGradient = () => {
    if (isBitcoin) return 'from-orange-500/10 to-yellow-500/5 border-orange-400/20';
    if (isKaspa) return 'from-blue-500/10 to-cyan-500/5 border-blue-400/20';
    if (isAlph) return 'from-purple-500/10 to-pink-500/5 border-purple-400/20';
    return 'from-blue-500/10 to-cyan-500/5 border-blue-500/20';
  };

  return (
    <motion.div
      className="relative cursor-pointer group"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
      }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.1,
      }}
      style={{
        zIndex: isHovered ? 50 : totalIcons - index,
      }}
    >
      {/* Icon container */}
      <div
        className={`relative ${iconSize} rounded-2xl bg-gradient-to-br ${getGradient()} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:shadow-xl`}
        style={{
          transform: isHovered && !isMobile ? 'translateY(-8px) scale(1.05)' : 'none',
        }}
      >
        {/* Icon */}
        <img
          src={src}
          alt={alt}
          className={`relative ${iconInnerSize} object-contain transition-transform duration-300 group-hover:scale-110`}
          loading="lazy"
        />

        {/* Bitcoin crown badge */}
        {isBitcoin && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full flex items-center justify-center shadow-md">
            <div className="text-xs font-bold text-white">#1</div>
          </div>
        )}
      </div>

      {/* Label */}
      <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 px-3 py-1.5 rounded-lg backdrop-blur-sm border ${
        isBitcoin ? 'bg-orange-50/90 border-orange-200/50' :
        isKaspa ? 'bg-blue-50/90 border-blue-200/50' :
        'bg-purple-50/90 border-purple-200/50'
      } shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}>
        <span className={`text-xs font-medium whitespace-nowrap ${
          isBitcoin ? 'text-orange-800' :
          isKaspa ? 'text-blue-800' :
          'text-purple-800'
        }`}>
          {alt}
        </span>
      </div>
    </motion.div>
  );
});

CryptoIcon.displayName = 'CryptoIcon';

export const CryptoIconsRow = memo(() => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const icons = [
    { src: "/assets/bitcoin-btc-logo.svg", alt: "Bitcoin" },
    { src: "/assets/kaspa-kas-logo.svg", alt: "Kaspa" },
    { src: "/assets/currency-alph.png", alt: "Alephium" },
  ];

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center px-4 bg-gradient-to-t from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-950">
  {/* Simple background effects */}
  <div className="absolute inset-0 overflow-hidden">
    {/* Light mode background effects */}
    <div className="absolute top-1/4 left-1/4 w-64 h-64 md:w-96 md:h-96 bg-gradient-to-r from-blue-100/60 to-cyan-100/40 blur-3xl rounded-full dark:from-blue-600/5 dark:to-cyan-500/3" />
    <div className="absolute bottom-1/4 right-1/4 w-64 h-64 md:w-96 md:h-96 bg-gradient-to-r from-purple-100/60 to-pink-100/40 blur-3xl rounded-full dark:from-purple-600/5 dark:to-pink-500/3" />
  </div>

  {/* Main container - centered */}
  <div className="relative w-full max-w-4xl mx-auto text-center">
    {/* Header - centered */}
    <motion.div 
      className="mb-8 md:mb-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-4">
        Maximize Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">Mining ROI</span> with Premium Networks
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
        Tap into the highest-yielding blockchain networks with our institutional-grade 
        mining solutions. Global infrastructure, enterprise security, and maximum uptime.
      </p>
    </motion.div>

    {/* Icons row - centered */}
    <div className="flex justify-center items-center gap-6 md:gap-12 lg:gap-16">
      {icons.map((icon, index) => (
        <CryptoIcon
          key={index}
          src={icon.src}
          alt={icon.alt}
          index={index}
          isHovered={hoveredIndex === index}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          totalIcons={icons.length}
          isMobile={isMobile}
        />
      ))}
    </div>

    {/* Simple connecting line - desktop only */}
    {!isMobile && (
      <div className="relative h-px w-full max-w-md mx-auto mt-8">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent dark:via-blue-500/20" />
      </div>
    )}

    {/* Simple info text - centered */}
    <motion.div 
      className="mt-8 md:mt-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/70 backdrop-blur-sm border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-sm text-slate-700 dark:text-slate-300">
          All networks operational
        </span>
      </div>
    </motion.div>
  </div>
</div>
  );
});

CryptoIconsRow.displayName = 'CryptoIconsRow';